import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Calendar, Clock } from 'lucide-react';
import { BLOG_INDEX, BLOG_POSTS } from '@/data/blog';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';

/* Heritage Estate Blog Series — three long-form articles supporting the
 * Juliet 2 launch. Same navigation + footer as the rest of the site,
 * Highland-themed hero image per post. */

function formatDate(iso) {
  const d = new Date(iso);
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
}

export function BlogIndex() {
  return (
    <>
      <Navigation />
      <main
        data-testid="blog-index-page"
        className="bg-[var(--brand-parchment)] text-[var(--brand-forest)] pt-32 pb-24 min-h-screen"
      >
        <div className="max-w-5xl mx-auto px-6 lg:px-10">
          <span className="font-eyebrow">Heritage Estate Blog Series</span>
          <h1 className="mt-3 font-display text-5xl sm:text-6xl lg:text-7xl leading-[1.02]">
            Field notes from <span className="italic text-[var(--brand-gold-soft)]">the hill.</span>
          </h1>
          <p className="mt-6 max-w-2xl text-[var(--brand-muted)] text-lg leading-relaxed">
            Long-form thinking about the kit we choose, why we choose it, and what
            we learn on the ground. Written by the founder, edited slowly, published
            when it&rsquo;s ready.
          </p>

          <div className="mt-16 grid gap-12">
            {BLOG_INDEX.map((post) => (
              <Link
                to={`/blog/${post.slug}`}
                key={post.slug}
                data-testid={`blog-card-${post.slug}`}
                className="group grid md:grid-cols-[1.2fr_1fr] gap-8 items-start border-t border-[var(--brand-olive)]/30 pt-10 hover:opacity-95"
              >
                <div className="aspect-[16/10] overflow-hidden">
                  <img
                    src={post.heroImage}
                    alt={post.heroAlt}
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-[900ms] group-hover:scale-[1.03]"
                  />
                </div>
                <div>
                  <div className="flex items-center gap-4 text-xs uppercase tracking-[0.2em] text-[var(--brand-muted)]">
                    <span className="inline-flex items-center gap-1.5">
                      <Calendar size={12} /> {formatDate(post.date)}
                    </span>
                    <span className="inline-flex items-center gap-1.5">
                      <Clock size={12} /> {post.readMins} min read
                    </span>
                  </div>
                  <h2 className="mt-3 font-display text-2xl sm:text-3xl leading-tight group-hover:text-[var(--brand-gold-soft)] transition-colors">
                    {post.title}
                  </h2>
                  <p className="mt-3 text-[var(--brand-muted)] leading-relaxed">{post.dek}</p>
                  <span className="mt-4 inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-[var(--brand-forest)]">
                    Read the article <ArrowRight size={14} />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}

export function BlogPost() {
  const { slug } = useParams();
  const post = BLOG_POSTS.find((p) => p.slug === slug);

  if (!post) {
    return (
      <>
        <Navigation />
        <main className="bg-[var(--brand-parchment)] pt-48 pb-24 min-h-screen text-center">
          <p className="font-display text-3xl text-[var(--brand-forest)]">Article not found.</p>
          <Link to="/blog" className="mt-6 inline-block link-expand text-sm font-semibold text-[var(--brand-forest)]">
            Back to the blog
          </Link>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Navigation />
      <main data-testid={`blog-post-${post.slug}`} className="bg-[var(--brand-parchment)] text-[var(--brand-ink)] pb-24">
        {/* Highland-themed hero */}
        <div className="relative h-[52svh] min-h-[380px] w-full overflow-hidden">
          <img src={post.heroImage} alt={post.heroAlt} className="absolute inset-0 w-full h-full object-cover" />
          <div
            aria-hidden="true"
            className="absolute inset-0"
            style={{
              background:
                'linear-gradient(180deg, rgba(4,20,10,0.55) 0%, rgba(4,20,10,0.35) 40%, rgba(4,20,10,0.80) 100%)',
            }}
          />
          <div className="absolute inset-x-0 bottom-0 max-w-4xl mx-auto px-6 lg:px-10 pb-10 text-[var(--brand-parchment)]">
            <Link
              to="/blog"
              className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-semibold text-[var(--brand-gold)] [text-shadow:0_1px_8px_rgba(0,0,0,0.55)]"
            >
              <ArrowLeft size={14} /> Blog Series
            </Link>
            <h1 className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] [text-shadow:0_2px_20px_rgba(0,0,0,0.55)]">
              {post.title}
            </h1>
            <div className="mt-4 flex items-center gap-5 text-xs uppercase tracking-[0.2em] text-[var(--brand-gold-soft)] [text-shadow:0_1px_8px_rgba(0,0,0,0.55)]">
              <span className="inline-flex items-center gap-1.5"><Calendar size={12} /> {formatDate(post.date)}</span>
              <span className="inline-flex items-center gap-1.5"><Clock size={12} /> {post.readMins} min read</span>
            </div>
          </div>
        </div>

        <article className="max-w-2xl mx-auto px-6 lg:px-8 pt-12 leading-[1.75] text-[17px] text-[var(--brand-ink)]">
          <p className="font-display italic text-2xl text-[var(--brand-forest)] leading-snug">{post.dek}</p>
          {post.body.map((para, i) => (
            <p key={i} className="mt-6">{para}</p>
          ))}
          <div className="mt-14 pt-8 border-t border-[var(--brand-olive)]/30 text-sm italic text-[var(--brand-muted)]">
            &mdash; The Founder &middot; Perthshire
          </div>
          <div className="mt-10">
            <Link
              to="/blog"
              data-testid="blog-back-link"
              className="inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-[0.18em] text-[var(--brand-forest)] link-expand"
            >
              <ArrowLeft size={14} /> Back to the series
            </Link>
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}
